var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { createContext, useContext, useState, memo, useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { twMerge } from 'tailwind-merge';
import { Slot } from '@radix-ui/react-slot';
const CarouselContext = createContext(null);
export const useCarouselContext = () => {
    const context = useContext(CarouselContext);
    if (!context) {
        throw new Error('useCarouselContext must be used within CarouselContext');
    }
    return context;
};
export const CarouselRoot = memo((props) => {
    const { className, children } = props, rest = __rest(props, ["className", "children"]);
    const [emblaRef, emblaApi] = useEmblaCarousel(rest, props.plugins);
    return (
    /* eslint-disable-next-line react/jsx-no-constructed-context-values */
    <CarouselContext.Provider value={{ emblaApi, emblaRef }}>
			<div className={twMerge('embla w-full', className)}>
				{children}
			</div>
		</CarouselContext.Provider>);
});
export const CarouselContainer = memo((_a) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    const { emblaRef } = useCarouselContext();
    return (<div className="embla__viewport overflow-hidden" ref={emblaRef}>
			<div className={twMerge(className, 'embla__container')} {...props}/>
		</div>);
});
/**
 * @description
 * Need to set up a slide width
 */
export const CarouselSlide = memo((_a) => {
    var { className, children, width } = _a, props = __rest(_a, ["className", "children", "width"]);
    return (<div className={twMerge(className, 'embla__slide')} style={{ flex: `0 0 ${width}` }} {...props}>
			{children}
		</div>);
});
export const CarouselPrevButton = memo((_a) => {
    var { className, children, asChild } = _a, props = __rest(_a, ["className", "children", "asChild"]);
    const { isPrevDisabled, onPrevButtonClick } = usePrevNextButtons();
    const Comp = asChild ? Slot : 'button';
    return (<Comp type="button" className={twMerge(className, 'embla__prev')} onClick={onPrevButtonClick} disabled={isPrevDisabled} {...props}>
			{children}
		</Comp>);
});
export const CarouselNextButton = memo((_a) => {
    var { className, children, asChild } = _a, props = __rest(_a, ["className", "children", "asChild"]);
    const { isNextDisabled, onNextButtonClick } = usePrevNextButtons();
    const Comp = asChild ? Slot : 'button';
    return (<Comp type="button" className={twMerge(className, 'embla__next')} onClick={onNextButtonClick} disabled={isNextDisabled} {...props}>
			{children}
		</Comp>);
});
export const usePrevNextButtons = () => {
    const { emblaApi } = useCarouselContext();
    const [isPrevDisabled, setPrevDisabled] = useState(true);
    const [isNextDisabled, setNextDisabled] = useState(true);
    const onPrevButtonClick = () => {
        if (!emblaApi)
            return;
        emblaApi.scrollPrev();
    };
    const onNextButtonClick = () => {
        if (!emblaApi)
            return;
        emblaApi.scrollNext();
    };
    const onSelect = (eventEmblaApi) => {
        setPrevDisabled(!eventEmblaApi.canScrollPrev());
        setNextDisabled(!eventEmblaApi.canScrollNext());
    };
    useEffect(() => {
        if (!emblaApi)
            return;
        onSelect(emblaApi);
        emblaApi
            .on('reInit', onSelect)
            .on('select', onSelect);
    }, [emblaApi]);
    return {
        isNextDisabled,
        isPrevDisabled,
        onPrevButtonClick,
        onNextButtonClick,
    };
};
export const Root = CarouselRoot;
export const Container = CarouselContainer;
export const Slide = CarouselSlide;
export const PrevButton = CarouselPrevButton;
export const NextButton = CarouselNextButton;
